import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Bar,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { extractDateString } from '../../../lib/date-utils.js';
import type { BugDetectionPoint } from '../../../hooks/useBugDetectionDistribution.js';

/**
 * 健全と判定する検知日数の上限（この日数以内に発見できていれば健全）。
 * @graph-connects none
 */
export const HEALTHY_THRESHOLD_DAYS = 7;
/**
 * Y 軸の上限。超過バグは最上部（360日）に丸めて表示し、軸は伸ばさない。
 * @graph-connects none
 */
export const Y_AXIS_MAX_DAYS = 360;
/** 水色（健全ライン）。 @graph-connects none */
const HEALTHY_LINE_COLOR = '#38bdf8';
/** 健全スポット色（青）。 @graph-connects none */
const HEALTHY_SPOT_COLOR = '#2563eb';

/**
 * 検知日数に応じたスポット色を返す。データエンコード用の色スケールのため CSS 変数ではなく
 * 数値 RGB で補間する。
 * - 7日以内: 健全な青（HEALTHY_SPOT_COLOR）
 * - 8〜360日: 黄 (250,204,21) → 赤 (239,68,68) のグラデーション（日数が多いほど赤に近づく）
 * @graph-connects none
 */
export function detectionDaysColor(days: number): string {
  if (days <= HEALTHY_THRESHOLD_DAYS) return HEALTHY_SPOT_COLOR;
  const span = Y_AXIS_MAX_DAYS - (HEALTHY_THRESHOLD_DAYS + 1);
  const ratio = Math.min(Math.max((days - (HEALTHY_THRESHOLD_DAYS + 1)) / span, 0), 1);
  const r = Math.round(250 + (239 - 250) * ratio);
  const g = Math.round(204 + (68 - 204) * ratio);
  const b = Math.round(21 + (68 - 21) * ratio);
  return `rgb(${String(r)}, ${String(g)}, ${String(b)})`;
}

interface SpotShapeProps {
  cx?: number;
  cy?: number;
  /** recharts が各シンボルに渡す描画順インデックス（スタッガー遅延に使用）。 */
  index?: number;
  payload?: { detectionDays?: number };
}

/**
 * 各バグを検知日数に応じた色の円スポットで描画する Scatter shape レンダラ。
 * 初回描画時は下から浮き上がる気泡アニメーション（animate-spot-rise）を適用し、
 * index をもとに波打つようスタッガー遅延させる（10 件周期で 0〜360ms）。
 * @graph-connects detectionDaysColor [calls] 検知日数→色変換
 */
function renderSpot(props: SpotShapeProps): ReactElement {
  const { cx, cy, index, payload } = props;
  if (cx == null || cy == null) return <g />;
  const days = payload?.detectionDays ?? 0;
  const delayMs = ((index ?? 0) % 10) * 40;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill={detectionDaysColor(days)}
      fillOpacity={0.85}
      stroke="var(--color-bg-elevated)"
      strokeWidth={0.75}
      className="animate-spot-rise"
      style={{ animationDelay: `${String(delayMs)}ms` }}
    />
  );
}

interface BugDetectionScatterChartProps {
  /** 検知日数 > 0 の個別スポット（バグ 1 件 = 1 点）。 */
  points: BugDetectionPoint[];
  /** その日の領域クリック時に発見日を渡す詳細モーダル表示コールバック。 */
  onPointClick: (date: unknown) => void;
}

interface ChartClickState {
  activeLabel?: string | number;
}

/** ツールチップに渡す 1 バグの表示情報。 */
interface DayBug {
  detectionDays: number;
  issueKey: string;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ検知日数分布チャート。案件起因バグを1件=1スポットで散布表示し、発見日と起因案件リリース完了日の差分日数をY軸(0〜360日、超過は最上部に丸め)にとる。7日以内は健全な青、8〜360日は黄→赤グラデーションで色付けし、7日に水色の健全ラインを引く。日付列にホバーするとその日の全バグを一覧表示し、列クリックで発見日を詳細モーダルへ連携する
 * @graph-connects ComposedChart [calls] バグ検知日数分布チャート描画
 * @graph-connects detectionDaysColor [calls] 検知日数→スポット色変換
 * @graph-connects extractDateString [calls] 日付文字列正規化
 */
export function BugDetectionScatterChart({ points, onPointClick }: BugDetectionScatterChartProps) {
  const { t } = useTranslation();

  // 個別スポット（バグ 1 件）。カテゴリ軸なので同日は同じ x に縦に並ぶ。
  const bugPoints = points
    .map((p) => ({
      date: extractDateString(p.date),
      // 360日超は最上部（Y_AXIS_MAX_DAYS）に丸めて描画。実日数は detectionDays に保持しツールチップで表示。
      y: Math.min(p.detectionDays, Y_AXIS_MAX_DAYS),
      detectionDays: p.detectionDays,
      issueKey: p.issueKey,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // 日別集約。透明フル高バー（列全体のホバー領域）と、ツールチップの全件表示に使う。
  const byDay = new Map<string, DayBug[]>();
  for (const b of bugPoints) {
    const list = byDay.get(b.date) ?? [];
    list.push({ detectionDays: b.detectionDays, issueKey: b.issueKey });
    byDay.set(b.date, list);
  }
  const dayRows = [...byDay.keys()].sort().map((date) => ({ date, hoverBand: Y_AXIS_MAX_DAYS }));

  // スポット上でなくても、その日の列クリックで発見日を詳細モーダルへ渡す。
  const handleChartClick = (state: ChartClickState) => {
    if (state.activeLabel != null) onPointClick(state.activeLabel);
  };

  const xTickFormatter = (value: string) => {
    const date = new Date(extractDateString(value));
    if (isNaN(date.getTime())) return String(value);
    return `${String(date.getMonth() + 1)}/${String(date.getDate())}`;
  };
  const yTickFormatter = (value: number) => t('kpi.daysUnit', { days: value });

  // ホバー中の日の全バグ（発見日・チケット・検知日数）を 1 つのボックスに一覧表示する。
  // スポット上でも列の空白でも、同じ「その日の全件」を表示する。
  const renderTooltip = (props: {
    active?: boolean;
    label?: string | number;
  }): ReactElement | null => {
    const date = props.active && props.label != null ? extractDateString(props.label) : '';
    const bugs = date ? byDay.get(date) : undefined;
    if (!date || !bugs || bugs.length === 0) return null;
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm shadow-lg">
        <div className="mb-1 font-medium text-[var(--color-text-secondary)]">
          {date}（{t('kpi.detail.bugDetectionCount', { n: bugs.length })}）
        </div>
        <ul className="space-y-0.5">
          {bugs.map((b) => (
            <li key={b.issueKey} className="text-[var(--color-text-primary)]">
              {b.issueKey}: {t('kpi.daysUnit', { days: b.detectionDays })}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <ComposedChart
      data={dayRows}
      margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
      onClick={handleChartClick as never}
      style={{ cursor: 'pointer' }}
    >
      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
      <XAxis
        dataKey="date"
        allowDuplicatedCategory={false}
        stroke="var(--color-text-muted)"
        fontSize={11}
        tickFormatter={xTickFormatter}
      />
      <YAxis
        type="number"
        domain={[0, Y_AXIS_MAX_DAYS]}
        allowDataOverflow
        ticks={[0, HEALTHY_THRESHOLD_DAYS, 90, 180, 270, Y_AXIS_MAX_DAYS]}
        stroke="var(--color-text-muted)"
        fontSize={11}
        tickFormatter={yTickFormatter}
      />
      <ReferenceLine y={HEALTHY_THRESHOLD_DAYS} stroke={HEALTHY_LINE_COLOR} strokeDasharray="4 4" />
      <Tooltip content={renderTooltip as never} cursor={{ fill: 'var(--color-bg-hover)' }} />
      {/* 列全体をホバー/クリック可能にする透明フル高バー（その日の全バグをツールチップ表示） */}
      <Bar dataKey="hoverBand" fill="transparent" isAnimationActive={false} />
      <Scatter
        data={bugPoints}
        dataKey="y"
        name="detectionDays"
        shape={renderSpot as never}
        isAnimationActive={false}
      />
    </ComposedChart>
  );
}
